import React from 'react';
import { FolderIcon } from '@heroicons/react/24/outline';
import { Appointment, Case } from '../../types';
import { t } from '../../utils/translations';
import CaseListCard from './CaseListCard';

interface CasesGridProps {
  cases: Case[];
  loading: boolean;
  caseNotes: Record<string, string>;
  caseReportsCount: Record<string, number>;
  /** Session numbers with a tablou already, per caseId — for the session road's "Vezi tabloul" CTA. */
  caseTabloulSessions?: Record<string, Set<number>>;
  nextAppointmentByCaseId?: Record<string, Appointment>;
  appointmentBySessionByCaseId?: Record<string, Record<number, Appointment>>;
  /** Case ids with a past meeting that has no session report yet. */
  reportsNeededCaseIds?: Set<string>;
  /** Case ids still being asked for the last session's tablou. */
  askTabloulCaseIds?: Set<string>;
  /** Overdue cadence duration per case id, when the cadence is exceeded. */
  frequencyOverdueByCaseId?: Record<string, string>;
  pulseAppointment?: boolean;
  hasActiveFilters: boolean;
  onOpenNotes: (caseItem: Case) => void;
  onAddNote: (caseItem: Case) => void;
  onOpenAddReport?: (caseItem: Case) => void;
  onOpenReports?: (caseItem: Case) => void;
  onOpenReportSession?: (caseItem: Case, session: number) => void;
  onSchedule?: (caseItem: Case, session?: number) => void;
  onOpenTimeline: (caseItem: Case) => void;
  onDeepen?: (caseItem: Case) => void;
  onCloseCase?: (caseItem: Case) => void;
  onEdit?: (caseItem: Case) => void;
  onReassign?: (caseItem: Case) => void;
  onEditDescription?: (caseItem: Case) => void;
  onOpenDescription: (caseItem: Case) => void;
  onOpenAppointment?: (appointment: Appointment) => void;
  onDelete?: (caseItem: Case) => void;
  hideMeetingNotes?: boolean;
  showCaseId?: boolean;
  blurSensitiveContent?: boolean;
  /** Off where session counts are not loaded (admin case management). */
  showSessionPath?: boolean;
  /** Admin inventory does not load per-case counts, but leaders still need the reports entry. */
  alwaysShowReports?: boolean;
  /** Name the counsellor on each card — for lists that span the whole team. */
  showAssignedCounselor?: boolean;
}

const CasesGrid: React.FC<CasesGridProps> = ({
  cases,
  loading,
  caseNotes,
  caseReportsCount,
  caseTabloulSessions,
  nextAppointmentByCaseId,
  appointmentBySessionByCaseId,
  reportsNeededCaseIds,
  askTabloulCaseIds,
  frequencyOverdueByCaseId,
  pulseAppointment = false,
  hasActiveFilters,
  onOpenNotes,
  onAddNote,
  onOpenAddReport,
  onOpenReports,
  onOpenReportSession,
  onSchedule,
  onOpenTimeline,
  onDeepen,
  onCloseCase,
  onEdit,
  onReassign,
  onEditDescription,
  onOpenDescription,
  onOpenAppointment,
  onDelete,
  hideMeetingNotes = false,
  showCaseId = false,
  blurSensitiveContent = false,
  showSessionPath = true,
  alwaysShowReports = false,
  showAssignedCounselor = false,
}) => {
  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-64 animate-pulse rounded-xl bg-slate-100" />
        ))}
      </div>
    );
  }

  if (cases.length === 0) {
    return (
      <div className="flex min-h-[300px] flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-white p-8 text-center">
        <FolderIcon className="mb-4 h-14 w-14 text-slate-300" />
        <h2 className="text-lg font-semibold text-slate-700">{t.cases.noCases}</h2>
        <p className="mt-1 max-w-sm text-sm text-slate-500">
          {hasActiveFilters ? t.cases.noCasesMessage : t.cases.noCasesMessage}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {cases.map((caseItem) => {
        const nextAppointment = nextAppointmentByCaseId?.[caseItem.id];
        return (
          <CaseListCard
            key={caseItem.id}
            caseItem={caseItem}
            latestNote={caseNotes[caseItem.id] ?? ''}
            reportsCount={caseReportsCount[caseItem.id] ?? 0}
            tabloulSessions={caseTabloulSessions?.[caseItem.id]}
            nextAppointment={nextAppointment}
            appointmentBySession={appointmentBySessionByCaseId?.[caseItem.id]}
            needsReport={reportsNeededCaseIds?.has(caseItem.id) ?? false}
            askTablou={askTabloulCaseIds?.has(caseItem.id) ?? false}
            frequencyOverdueBy={frequencyOverdueByCaseId?.[caseItem.id] ?? null}
            pulseAppointment={pulseAppointment}
            onOpenNotes={() => onOpenNotes(caseItem)}
            onAddNote={() => onAddNote(caseItem)}
            onOpenAddReport={
              onOpenAddReport ? () => onOpenAddReport(caseItem) : undefined
            }
            onOpenReports={onOpenReports ? () => onOpenReports(caseItem) : undefined}
            onOpenReportSession={
              onOpenReportSession
                ? (session) => onOpenReportSession(caseItem, session)
                : undefined
            }
            onSchedule={
              onSchedule ? (session) => onSchedule(caseItem, session) : undefined
            }
            onOpenTimeline={() => onOpenTimeline(caseItem)}
            onDeepen={onDeepen ? () => onDeepen(caseItem) : undefined}
            onCloseCase={onCloseCase ? () => onCloseCase(caseItem) : undefined}
            onEdit={onEdit ? () => onEdit(caseItem) : undefined}
            onReassign={onReassign ? () => onReassign(caseItem) : undefined}
            onEditDescription={
              onEditDescription ? () => onEditDescription(caseItem) : undefined
            }
            onOpenDescription={() => onOpenDescription(caseItem)}
            onOpenAppointment={
              nextAppointment && onOpenAppointment
                ? () => onOpenAppointment(nextAppointment)
                : undefined
            }
            onDelete={onDelete ? () => onDelete(caseItem) : undefined}
            hideMeetingNotes={hideMeetingNotes}
            showCaseId={showCaseId}
            blurSensitiveContent={blurSensitiveContent}
            showSessionPath={showSessionPath}
            alwaysShowReports={alwaysShowReports}
            showAssignedCounselor={showAssignedCounselor}
          />
        );
      })}
    </div>
  );
};

export default CasesGrid;
